import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import API from "api/index";
import { addNotification } from './notification.slice';

// ✅ Create a new location
export const createLocation = createAsyncThunk(
    "locations/createLocation",
    async (locationData, { rejectWithValue, dispatch }) => {
        try {
            const response = await API.post('/travel/travelLocation', locationData);
            dispatch(addNotification({ message: "Location created successfully!", type: "success" }));
            return response.data;
        } catch (error) {
            const errMsg = error.response?.data?.message || "Failed to create location";
            dispatch(addNotification({ message: errMsg, type: "error" }));
            return rejectWithValue(errMsg);
        }
    }
);

export const updateLocation = createAsyncThunk(
    "locations/updateLocation",
    async ({ locationId, formData }, { rejectWithValue, dispatch }) => {
        try {
            const response = await API.put(`/travel/travelLocation/${locationId}`, formData);
            dispatch(addNotification({ message: "Location updated successfully!", type: "success" }));
            return response.data.updatedLocation;
        } catch (error) {
            const errMsg = error.response?.data?.message || "Failed to update location";
            dispatch(addNotification({ message: errMsg, type: "error" }));
            return rejectWithValue(errMsg);
        }
    }
);



// ✅ Fetch all locations
export const fetchLocations = createAsyncThunk(
    "locations/fetchLocations",
    async (_, { rejectWithValue }) => {
        try {
            const response = await API.get("/travel/travelLocation");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch locations");
        }
    }
);

export const softDeleteLocation = createAsyncThunk(
    "locations/softDeleteLocation",
    async (locationId, { rejectWithValue, dispatch }) => {
        try {
            const response = await API.put(`/travel/travelLocation/soft-delete/${locationId}`);
            dispatch(addNotification({ message: "Location moved to Trash!", type: "success" }));
            return locationId;  // ✅ Ensure correct ID is returned
        } catch (error) {
            const errMsg = error.response?.data?.message || "Failed to delete location";
            dispatch(addNotification({ message: errMsg, type: "error" }));
            return rejectWithValue(errMsg);
        }
    }
);


export const fetchTrashedLocations = createAsyncThunk(
    "locations/fetchTrashedLocations",
    async (_, { rejectWithValue }) => {
        try {
            const response = await API.get("/travel/travelLocation/trashed");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch trashed locations");
        }
    }
);


// ✅ Restore location from Trash
export const restoreLocation = createAsyncThunk(
    "locations/restoreLocation",
    async (locationId, { rejectWithValue, dispatch }) => {
        try {
            const response = await API.put(`/travel/travelLocation/restore/${locationId}`);
            dispatch(addNotification({ message: "Location restored successfully!", type: "success" }));
            return response.data;
        } catch (error) {
            const errMsg = error.response?.data?.message || "Failed to restore location";
            dispatch(addNotification({ message: errMsg, type: "error" }));
            return rejectWithValue(errMsg);
        }
    }
);

export const permanentDeleteLocation = createAsyncThunk(
    'travel/permanentDeleteLocation',
    async (id, { rejectWithValue }) => {
        try {
            await API.delete(`/travel/travelLocation/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to permanently delete location");
        }
    }
);




const travelLocationSlice = createSlice({
    name: 'travelLocations',
    initialState: {
        locations: [],
        trashedLocations: [],
        selectedLocation: null,
        loading: false,
        error: null,
    },
    reducers: {
        setSelectedLocation: (state, action) => {
            state.selectedLocation = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create Location
            .addCase(createLocation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createLocation.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload?.location || action.payload;
                if (payload) state.locations.push(payload);
            })
            .addCase(createLocation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            .addCase(updateLocation.fulfilled, (state, action) => {
                const updatedLocation = action.payload;
                state.locations = state.locations.map(loc =>
                    loc._id === updatedLocation._id ? updatedLocation : loc
                );
            })

            // Fetch Locations
            .addCase(fetchLocations.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLocations.fulfilled, (state, action) => {
                state.loading = false;
                state.locations = action.payload;
            })
            .addCase(fetchLocations.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Soft Delete Location
            .addCase(softDeleteLocation.fulfilled, (state, action) => {
                state.locations = state.locations.filter(loc => loc._id !== action.payload);
                state.trashedLocations.push(
                    state.locations.find(loc => loc._id === action.payload)
                );
            })


            // Fetch Trashed Locations
            .addCase(fetchTrashedLocations.fulfilled, (state, action) => {
                state.trashedLocations = action.payload;
            })

            // Restore Location
            .addCase(restoreLocation.fulfilled, (state, action) => {
                state.trashedLocations = state.trashedLocations.filter(
                    loc => loc._id !== action.payload._id
                );
                state.locations.push(action.payload);
            })
            .addCase(permanentDeleteLocation.fulfilled, (state, action) => {
                state.trashedLocations = state.trashedLocations.filter(
                    (loc) => loc._id !== action.payload
                );
            });
    }
});

export const { setSelectedLocation } = travelLocationSlice.actions;
export default travelLocationSlice.reducer;
